import { useLayoutEffect, useMemo } from "react";
import { UserDetailsRequirement } from "types";
import { useCreatePurchaseStore } from "../../../stores/createPurchaseStore";
import { CheckoutFormValues } from "../CheckoutPurchaseScreen.types";
import { getPurchaserValues } from "../helpers/PurchaseCreateHelpers";
import { useCustomerDetailsStore } from "../store/customerDetailsStore";
import { useCreatePurchaseGiftInitialValues } from "./useCreatePurchaseGiftInitialValues";
import { useGiftDetailsRequirements } from "./useGiftDetailsRequirements";
import { isCustomerNameRequired } from "@/models/Experience";

export const initialValues: CheckoutFormValues = {
    customerName: "",
    customerEmail: "",
    phone: "",
    tags: [],
    deposit: 0,
    comment: "",
    note: [{ text: "" }],
    arrivalOffset: 0,
    depositDuration: 0,
};

export const useCheckoutPurchaseInitialValues = (): CheckoutFormValues => {
    if (useCreatePurchaseStore.getState().purchaseId) {
        const { purchase } = useCreatePurchaseStore.getState();
        const { name: purchaserName, email: purchaserEmail, phone: purchaserPhone } = getPurchaserValues(purchase);

        const item = useCreatePurchaseStore.getState().getActiveItem();
        const { customerName, customerEmail, phone } = useCustomerDetailsStore.getState();

        return {
            customerName: customerName ?? purchaserName ?? "",
            customerEmail: customerEmail ?? purchaserEmail ?? "",
            phone: phone ?? purchaserPhone ?? "",
            tags: purchase.tags ?? [],
            note: purchase.notes ?? initialValues.note,
            comment: purchase.comment ?? "",
            deposit: purchase.payNow ?? 0,
            arrivalOffset: item.paymentDueOffset ?? 0,
            depositDuration: item.balanceDueOffset ?? 0,
        };
    }

    const organizer = useCreatePurchaseStore.getState().organizer;

    return {
        ...structuredClone(initialValues),
        customerName: organizer?.name ?? "",
        customerEmail: organizer?.email ?? "",
        phone: organizer?.phone ?? "",
    };
};

export const useCheckoutGiftPurchaseInitialValues = () => {
    const purchase = useCreatePurchaseStore((state) => state.purchase);
    const item = useCreatePurchaseStore().getActiveItem();
    const giftValues = useCreatePurchaseGiftInitialValues();
    const { organizerDetailsRequirement } = useGiftDetailsRequirements();

    useLayoutEffect(() => {
        useCustomerDetailsStore.setState({
            nameRequired: isCustomerNameRequired(organizerDetailsRequirement),
            showCustomerFields: organizerDetailsRequirement !== UserDetailsRequirement.NOT_COLLECTED,
        });
    }, [organizerDetailsRequirement]);

    return useMemo(() => {
        const { customerName, customerEmail, phone } = useCustomerDetailsStore.getState();

        return {
            ...initialValues,
            recipientName: "",
            recipientEmail: "",
            ...giftValues,
            customerName: customerName ?? "",
            customerEmail: customerEmail ?? "",
            phone: phone ?? "",
            tags: purchase.tags ?? [],
            note: purchase.notes ?? initialValues.note,
            comment: purchase.comment ?? "",
            deposit: purchase.payNow ?? 0,
            arrivalOffset: item.paymentDueOffset ?? 0,
            depositDuration: item.balanceDueOffset ?? 0,
        };
    }, [purchase, item, giftValues]);
};
